import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

import { debounceTime, fromEvent, Observable, of, Subscription } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { NotificationButtonStyle, NotificationTheme } from '@fcom/ui-components/components/notifications/enums';
import { StepperStep } from '@fcom/ui-components/components/stepper/interfaces';
import { StepperTheme } from '@fcom/ui-components/components/stepper/enums';
import { findScrollContainer } from '@fcom/core';

@Component({
  selector: 'fin-login-form-container',
  templateUrl: './login-form-container.component.html',
  styleUrls: ['./login-form-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LoginFormContainerComponent implements OnInit, AfterViewInit, OnDestroy {
  private cd = inject(ChangeDetectorRef);
  private renderer = inject(Renderer2);
  private document = inject<Document>(DOCUMENT);
  private elementRef = inject(ElementRef);
  private platformId = inject(PLATFORM_ID);

  private resizeSubscription: Subscription = undefined;

  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly StepperTheme = StepperTheme;
  readonly NotificationButtonStyle = NotificationButtonStyle;

  @ViewChild('dialogTitle') dialogTitle: ElementRef;
  @ViewChild('loginContainer') loginContainer: ElementRef;

  /** Popover top offset (px), used by the stylesheet to clamp max-height; `null` until measured. */
  readonly dialogTopOffsetPx = signal<number | null>(null);

  @Input()
  fullScreen = false;
  @Input()
  invertHeaderColors = false;
  @Input()
  closable = true;

  /**
   * A boolean flag indicating whether to use a compact representation
   * which removes some paddings and margins
   */
  @Input()
  compact = false;
  @Input()
  icon: SvgLibraryIcon;
  @Input()
  title: string;
  @Input()
  description: string;
  @Input()
  error: string;
  @Input()
  notificationTitle?: string;
  @Input()
  errorLinkText: string;
  @Input()
  stepperSteps$: Observable<StepperStep[] | undefined> = of(undefined);
  @Input()
  stepperActiveStep$: Observable<number> = of(0);
  @Input()
  overlay = false;
  @Input()
  showLeftIcon = false;

  @Output()
  stepperClicked: EventEmitter<StepperStep> = new EventEmitter<StepperStep>();

  @Output()
  returnClicked = new EventEmitter<Event>();
  @Output()
  closeClicked = new EventEmitter<Event>();
  @Output()
  alertNotificationLinkClicked = new EventEmitter<Event>();
  @Output()
  markupErrorLinkClicked = new EventEmitter<Event>();

  /**
   * The error text is rendered via [innerHTML] from a translation, so any link inside it cannot
   * carry an Angular binding. We delegate here: if the click originates from an anchor, emit so
   * the parent can react (e.g. close the login dialog).
   */
  onMarkupErrorContentClick(event: Event): void {
    const link = (event.target as Element)?.closest('a');
    if (link) {
      this.markupErrorLinkClicked.emit(event);
    }
  }

  ngOnInit(): void {
    this.renderer.addClass(this.document.body, 'modal-open-laptop-down');
    this.renderer.addClass(
      findScrollContainer(this.elementRef.nativeElement) || this.document.documentElement,
      'ios-scroll-touch'
    );

    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(this.document.body, 'modal-open-laptop-down');
    this.renderer.removeClass(
      findScrollContainer(this.elementRef.nativeElement) || this.document.documentElement,
      'ios-scroll-touch'
    );
    this.resizeSubscription?.unsubscribe();
    this.resizeSubscription = undefined;
    this.cd.detectChanges();
  }

  ngAfterViewInit(): void {
    this.dialogTitle.nativeElement.focus();

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.updateDialogTopOffset();

    this.resizeSubscription = fromEvent(window, 'resize')
      .pipe(debounceTime(150))
      .subscribe(() => this.updateDialogTopOffset());
  }

  private updateDialogTopOffset(): void {
    this.dialogTopOffsetPx.set(this.loginContainer.nativeElement.getBoundingClientRect().top);
  }
}
